import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "sonner";
import {
  fetchDiscoverPool,
  fetchShortlist,
  saveShortlist,
  fetchCampaigns,
  createCampaign,
  deleteCampaign as deleteCampaignApi,
  fetchBrandBadges,
} from "./services/brandModeServices";
import { emptyDeliverables, toPayloadItems } from "./brandUtils";

const BrandModeContext = createContext(null);

export const MAX_COMPARE = 10;

const DEFAULT_FILTERS = {
  sport: "",
  gender: "",
  states: [],
  tier: "",
  dealTypes: [],
  minScore: 0,
  search: "",
};

export const BrandModeProvider = ({ children }) => {
  // Discover pool (fetched once, filtered client-side).
  const [pool, setPool] = useState([]);
  const [options, setOptions] = useState(null);
  const [poolLoading, setPoolLoading] = useState(true);
  const [poolError, setPoolError] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // Compare selection (athlete ids, max 10). Survives navigation inside
  // Brand Mode: "everything you select stays with you until you send offers".
  const [compareIds, setCompareIds] = useState([]);

  // Working shortlist (server-backed; items come back enriched).
  const [shortlist, setShortlist] = useState({
    items: [],
    targetBudget: null,
    stats: null,
  });
  const [shortlistLoading, setShortlistLoading] = useState(true);
  const [shortlistSaving, setShortlistSaving] = useState(false);

  // Campaigns (V2 list with lifecycle status + chip counts).
  const [campaigns, setCampaigns] = useState([]);
  const [campaignCounts, setCampaignCounts] = useState({ all: 0, draft: 0, open: 0, in_progress: 0, completed: 0 });
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  // Header badges: unread athlete messages + active campaign count.
  const [badges, setBadges] = useState({ unread: 0, activeCampaigns: 0 });

  const refreshBadges = useCallback(async () => {
    try {
      const data = await fetchBrandBadges();
      if (data) setBadges({ unread: data.unread || 0, activeCampaigns: data.activeCampaigns || 0 });
    } catch (err) {
      // Badge counts are decorative; never block the shell on them.
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchDiscoverPool();
        if (cancelled) return;
        setPool(data?.athletes || []);
        setOptions(data?.options || null);
      } catch (err) {
        if (!cancelled) setPoolError(err.message || "Could not load athletes");
      } finally {
        if (!cancelled) setPoolLoading(false);
      }
    })();
    (async () => {
      try {
        const data = await fetchShortlist();
        if (!cancelled && data) setShortlist(data);
      } catch (err) {
        console.warn("Shortlist load failed:", err);
      } finally {
        if (!cancelled) setShortlistLoading(false);
      }
    })();
    refreshBadges();
    return () => {
      cancelled = true;
    };
  }, [refreshBadges]);

  const athleteById = useMemo(() => {
    const map = {};
    pool.forEach((a) => {
      map[a.athleteId] = a;
    });
    return map;
  }, [pool]);

  const filteredPool = useMemo(() => {
    const f = filters;
    const search = f.search.trim().toLowerCase();
    return pool.filter((a) => {
      if (f.sport && a.sport !== f.sport) return false;
      if (f.gender && a.gender !== f.gender) return false;
      if (f.states?.length && !f.states.includes(a.state)) return false;
      if (f.tier && a.visTier !== f.tier) return false;
      if (f.dealTypes?.length && !f.dealTypes.some((k) => (a.nilRates || {})[k])) return false;
      if (f.minScore && (a.cacheScore || 0) < f.minScore) return false;
      if (search) {
        const haystack = `${a.name} ${a.surname} ${a.school}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [pool, filters]);

  const activeFilterCount = useMemo(() => {
    const f = filters;
    return (
      (f.sport ? 1 : 0) +
      (f.gender ? 1 : 0) +
      (f.states?.length || 0) +
      (f.tier ? 1 : 0) +
      (f.dealTypes?.length || 0) +
      (f.minScore ? 1 : 0) +
      (f.search ? 1 : 0)
    );
  }, [filters]);

  const clearFilters = useCallback(() => setFilters(DEFAULT_FILTERS), []);

  // ----- Compare -----
  const isCompared = useCallback((athleteId) => compareIds.includes(athleteId), [compareIds]);

  const toggleCompare = useCallback((athleteId) => {
    setCompareIds((prev) => {
      if (prev.includes(athleteId)) return prev.filter((id) => id !== athleteId);
      if (prev.length >= MAX_COMPARE) {
        toast.error(`You can compare up to ${MAX_COMPARE} athletes.`);
        return prev;
      }
      return [...prev, athleteId];
    });
  }, []);

  const clearCompare = useCallback(() => setCompareIds([]), []);

  const comparedAthletes = useMemo(
    () => compareIds.map((id) => athleteById[id]).filter(Boolean),
    [compareIds, athleteById],
  );

  // ----- Shortlist -----
  const pushShortlist = useCallback(async (nextItems, nextBudget) => {
    setShortlistSaving(true);
    try {
      const data = await saveShortlist({
        items: toPayloadItems(nextItems),
        targetBudget: nextBudget,
      });
      if (data) setShortlist(data);
      return data;
    } catch (err) {
      toast.error(err.message || "Could not save shortlist");
      return null;
    } finally {
      setShortlistSaving(false);
    }
  }, []);

  const shortlistHas = useCallback(
    (athleteId) => shortlist.items.some((i) => i.athleteId === athleteId),
    [shortlist.items],
  );

  const addToShortlist = useCallback(
    (athleteIds) => {
      const ids = Array.isArray(athleteIds) ? athleteIds : [athleteIds];
      const fresh = ids.filter((id) => !shortlist.items.some((i) => i.athleteId === id));
      if (!fresh.length) return Promise.resolve(null);
      const next = [
        ...shortlist.items,
        ...fresh.map((id) => ({
          athleteId: id,
          deliverables: emptyDeliverables(),
          offer: null,
        })),
      ];
      return pushShortlist(next, shortlist.targetBudget);
    },
    [shortlist, pushShortlist],
  );

  const removeFromShortlist = useCallback(
    (athleteId) => pushShortlist(shortlist.items.filter((i) => i.athleteId !== athleteId), shortlist.targetBudget),
    [shortlist, pushShortlist],
  );

  const toggleShortlist = useCallback(
    (athleteId) => {
      if (shortlistHas(athleteId)) return removeFromShortlist(athleteId);
      return addToShortlist(athleteId);
    },
    [shortlistHas, addToShortlist, removeFromShortlist],
  );

  const updateShortlistItem = useCallback(
    (athleteId, patch) => {
      const next = shortlist.items.map((i) => (i.athleteId === athleteId ? { ...i, ...patch } : i));
      return pushShortlist(next, shortlist.targetBudget);
    },
    [shortlist, pushShortlist],
  );

  // Deal template: apply one deliverable mix to every shortlisted athlete.
  const applyDealTemplate = useCallback(
    (deliverables) => {
      const next = shortlist.items.map((i) => ({ ...i, deliverables: { ...deliverables } }));
      return pushShortlist(next, shortlist.targetBudget);
    },
    [shortlist, pushShortlist],
  );

  const setTargetBudget = useCallback(
    (value) => pushShortlist(shortlist.items, value),
    [shortlist, pushShortlist],
  );

  const clearShortlist = useCallback(() => pushShortlist([], null), [pushShortlist]);

  // ----- Campaigns -----
  const refreshCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    try {
      const data = await fetchCampaigns();
      setCampaigns(data?.campaigns || []);
      if (data?.counts) setCampaignCounts(data.counts);
      refreshBadges();
    } catch (err) {
      toast.error(err.message || "Could not load campaigns");
    } finally {
      setCampaignsLoading(false);
    }
  }, [refreshBadges]);

  // "Save as campaign" on Discover / Shortlist: snapshot the working
  // shortlist into a draft campaign the builder then opens.
  const createDraftFromShortlist = useCallback(
    async (name) => {
      const created = await createCampaign({
        name: name || `Campaign ${new Date().toLocaleDateString()}`,
        athleteIds: shortlist.items.map((i) => i.athleteId),
        budget: { total: shortlist.targetBudget || null, paymentStructure: "milestone" },
      });
      setCampaigns((prev) => [created, ...prev]);
      refreshBadges();
      return created;
    },
    [shortlist, refreshBadges],
  );

  const removeCampaign = useCallback(
    async (campaignId) => {
      await deleteCampaignApi(campaignId);
      setCampaigns((prev) => prev.filter((c) => c.id !== campaignId));
      refreshBadges();
    },
    [refreshBadges],
  );

  // "Edit in Shortlist": replace the working shortlist with a campaign's
  // roster so the user can rework and re-save it.
  const loadCampaignIntoShortlist = useCallback(
    (campaign) => {
      const items = (campaign.athleteIds || campaign.items?.map((i) => i.athleteId) || []).map((id) => ({
        athleteId: id,
        deliverables: emptyDeliverables(),
        offer: null,
      }));
      return pushShortlist(items, campaign.budget?.total ?? campaign.targetBudget ?? null);
    },
    [pushShortlist],
  );

  const value = {
    pool,
    filteredPool,
    options,
    poolLoading,
    poolError,
    filters,
    setFilters,
    clearFilters,
    activeFilterCount,
    athleteById,
    compareIds,
    comparedAthletes,
    isCompared,
    toggleCompare,
    clearCompare,
    shortlist,
    shortlistLoading,
    shortlistSaving,
    shortlistHas,
    addToShortlist,
    removeFromShortlist,
    toggleShortlist,
    updateShortlistItem,
    applyDealTemplate,
    setTargetBudget,
    clearShortlist,
    campaigns,
    campaignCounts,
    campaignsLoading,
    refreshCampaigns,
    createDraftFromShortlist,
    removeCampaign,
    loadCampaignIntoShortlist,
    badges,
    refreshBadges,
  };

  return <BrandModeContext.Provider value={value}>{children}</BrandModeContext.Provider>;
};

export const useBrandMode = () => {
  const ctx = useContext(BrandModeContext);
  if (!ctx) throw new Error("useBrandMode must be used inside BrandModeProvider");
  return ctx;
};
